import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../_model/api-response';
import {
  CreateHouseCurrencyPairDto,
  CreateHouseLiquidityAdjustmentDto,
  HouseCurrencyPairDto,
  HouseGatewayCustomerProfileDto,
  HouseInstantTradeAdminDto,
  HouseLedgerEntryDto,
  HouseLiquidityAdjustmentDto,
  HouseLiquidityAlertDto,
  HouseOrderAdminDto,
  HouseOverviewDto,
  HouseRateProviderOptionDto,
  HouseReconciliationRunDto,
  HouseWalletSummaryDto,
  HouseWithdrawalQuoteResponse,
  InitiateHouseWithdrawalDto,
  InitiateWithdrawalResponseDto,
  UpdateHouseCurrencyPairDto,
  UpsertHouseGatewayCustomerProfileDto,
  WithdrawalDto,
  WithdrawalFormConfigResponseDto
} from '../_model/house-management';

@Injectable({ providedIn: 'root' })
export class HouseManagementService {
  private readonly base = `${environment.apiBaseUrl}/admin/house`;

  constructor(private http: HttpClient) {}

  getOverview() {
    return this.http.get<ApiResponse<HouseOverviewDto>>(`${this.base}/overview`);
  }

  getWallets() {
    return this.http.get<ApiResponse<HouseWalletSummaryDto[]>>(`${this.base}/wallets`);
  }

  getWalletLedger(walletId: string, page = 1, rowsPerPage = 20) {
    return this.http.get<ApiResponse<HouseLedgerEntryDto[]>>(
      `${this.base}/wallets/${walletId}/ledger`,
      { params: this.params({ page, rowsPerPage }) }
    );
  }

  getCurrencyPairs(filters: { fromCurrency?: string; toCurrency?: string; status?: string } = {}) {
    return this.http.get<ApiResponse<HouseCurrencyPairDto[]>>(
      `${this.base}/currency-pairs`,
      { params: this.params(filters) }
    );
  }

  createCurrencyPair(payload: CreateHouseCurrencyPairDto) {
    return this.http.post<ApiResponse<HouseCurrencyPairDto>>(
      `${this.base}/currency-pairs`,
      payload
    );
  }

  updateCurrencyPair(id: string, payload: UpdateHouseCurrencyPairDto) {
    return this.http.put<ApiResponse<HouseCurrencyPairDto>>(
      `${this.base}/currency-pairs/${id}`,
      payload
    );
  }

  updateCurrencyPairStatus(id: string, status: string) {
    return this.http.put<ApiResponse<HouseCurrencyPairDto>>(
      `${this.base}/currency-pairs/${id}/status`,
      { status }
    );
  }

  getRateProviders() {
    return this.http.get<ApiResponse<HouseRateProviderOptionDto[]>>(
      `${this.base}/rate-providers`
    );
  }

  getLiquidityAdjustments(walletId?: string, page = 1, rowsPerPage = 20) {
    return this.http.get<ApiResponse<HouseLiquidityAdjustmentDto[]>>(
      `${this.base}/liquidity-adjustments`,
      { params: this.params({ walletId, page, rowsPerPage }) }
    );
  }

  createLiquidityAdjustment(payload: CreateHouseLiquidityAdjustmentDto) {
    return this.http.post<ApiResponse<HouseLiquidityAdjustmentDto>>(
      `${this.base}/liquidity-adjustments`,
      payload
    );
  }

  getOrders(filters: { status?: string; currency?: string; page?: number; rowsPerPage?: number } = {}) {
    return this.http.get<ApiResponse<HouseOrderAdminDto[]>>(
      `${this.base}/orders`,
      { params: this.params(filters) }
    );
  }

  getTrades(filters: { status?: string; currency?: string; page?: number; rowsPerPage?: number } = {}) {
    return this.http.get<ApiResponse<HouseInstantTradeAdminDto[]>>(
      `${this.base}/trades`,
      { params: this.params(filters) }
    );
  }

  getWithdrawals(filters: { status?: string; currency?: string; page?: number; rowsPerPage?: number } = {}) {
    return this.http.get<ApiResponse<WithdrawalDto[]>>(
      `${this.base}/withdrawals`,
      { params: this.params(filters) }
    );
  }

  getLiquidityAlerts(filters: {
    status?: string;
    severity?: string;
    currency?: string;
    page?: number;
    rowsPerPage?: number;
  } = {}) {
    return this.http.get<ApiResponse<HouseLiquidityAlertDto[]>>(
      `${this.base}/operations/liquidity-alerts`,
      { params: this.params(filters) }
    );
  }

  acknowledgeLiquidityAlert(alertId: string, note?: string | null) {
    return this.http.post<ApiResponse<HouseLiquidityAlertDto>>(
      `${this.base}/operations/liquidity-alerts/${alertId}/acknowledge`,
      { note: note || null }
    );
  }

  getReconciliationRuns(filters: { status?: string; page?: number; rowsPerPage?: number } = {}) {
    return this.http.get<ApiResponse<HouseReconciliationRunDto[]>>(
      `${this.base}/operations/reconciliation-runs`,
      { params: this.params(filters) }
    );
  }

  getReconciliationRun(runId: string) {
    return this.http.get<ApiResponse<HouseReconciliationRunDto>>(
      `${this.base}/operations/reconciliation-runs/${runId}`
    );
  }

  runReconciliation() {
    return this.http.post<ApiResponse<HouseReconciliationRunDto>>(
      `${this.base}/operations/reconciliation-runs`,
      {}
    );
  }

  getGatewayProfiles() {
    return this.http.get<ApiResponse<HouseGatewayCustomerProfileDto[]>>(
      `${this.base}/gateway-profiles`
    );
  }

  upsertGatewayProfile(providerCode: string, payload: UpsertHouseGatewayCustomerProfileDto) {
    return this.http.put<ApiResponse<HouseGatewayCustomerProfileDto>>(
      `${this.base}/gateway-profiles/${encodeURIComponent(providerCode)}`,
      payload
    );
  }

  getWithdrawalFormConfig(walletId: string, paymentMethod?: string | null) {
    return this.http.get<ApiResponse<WithdrawalFormConfigResponseDto>>(
      `${this.base}/withdrawals/form-config/${walletId}`,
      { params: this.params({ paymentMethod }) }
    );
  }

  quoteWithdrawal(walletId: string, amount: number, paymentMethod?: string | null) {
    return this.http.post<ApiResponse<HouseWithdrawalQuoteResponse>>(
      `${this.base}/withdrawals/quote`,
      { walletId, amount, paymentMethod: paymentMethod || null }
    );
  }

  initiateWithdrawal(payload: InitiateHouseWithdrawalDto) {
    return this.http.post<ApiResponse<InitiateWithdrawalResponseDto>>(
      `${this.base}/withdrawals/initiate`,
      payload
    );
  }

  private params(values: Record<string, unknown>): HttpParams {
    let params = new HttpParams();

    Object.entries(values).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;
      params = params.set(key, String(value));
    });

    return params;
  }
}
